import type { BillingAmount, BillingOperation } from "@wms/domain";

/**
 * UX3010-09 «Операции к расчёту»: money comes from the server as decimal strings with two digits; the total of the
 * selection is added up in whole cents (BigInt), never in floating point, and per currency — never across them.
 */
export function toCents(value: string | null | undefined): bigint {
  if (!value) return 0n;
  const text = value.trim();
  const negative = text.startsWith("-");
  const [whole, fraction = ""] = text.replace(/^[-+]/, "").split(".");
  const cents = BigInt(whole || "0") * 100n + BigInt((fraction + "00").slice(0, 2) || "0");
  return negative ? -cents : cents;
}

export function fromCents(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  return `${negative ? "-" : ""}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`;
}

/** «18 750,00»: groups of thousands and a comma, as on the printed forms. */
export function formatMoney(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const [whole, fraction = "00"] = value.split(".");
  const negative = whole.startsWith("-");
  const digits = whole.replace("-", "").replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${negative ? "−" : ""}${digits},${fraction.padEnd(2, "0").slice(0, 2)}`;
}

/** Sum of amounts by currency, e.g. of the selected operations. */
export function sumAmounts(lists: BillingAmount[][]): BillingAmount[] {
  const sums = new Map<string, { net: bigint; tax: bigint; gross: bigint }>();
  for (const list of lists) {
    for (const amount of list) {
      const row = sums.get(amount.currency) ?? { net: 0n, tax: 0n, gross: 0n };
      row.net += toCents(amount.net);
      row.tax += toCents(amount.tax);
      row.gross += toCents(amount.gross);
      sums.set(amount.currency, row);
    }
  }
  return [...sums.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, row]) => ({ currency, net: fromCents(row.net), tax: fromCents(row.tax), gross: fromCents(row.gross) }));
}

/** «18 750,00 KZT + 100,00 USD»; empty — «0,00». */
export function amountsWords(amounts: BillingAmount[], field: "gross" | "net" = "gross"): string {
  if (!amounts.length) return "0,00";
  return amounts.map((a) => `${formatMoney(a[field])}${a.currency ? ` ${a.currency}` : ""}`).join(" + ");
}

export function selectedAmounts(items: BillingOperation[], keys: string[]): BillingAmount[] {
  const wanted = new Set(keys);
  return sumAmounts(items.filter((i) => wanted.has(i.key)).map((i) => i.amounts));
}

/** «1 операция», «3 операции», «5 операций». */
export function operationsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word = mod10 === 1 && mod100 !== 11 ? "операция" : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "операции" : "операций";
  return `${n} ${word}`;
}
